export const BUG_REPORT_SCREENSHOT_BUCKET = "bug_report_screenshots";
export const BUG_REPORT_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const BUG_REPORT_ATTACHMENT_ACCEPT = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(",");

const ACCEPTED_ATTACHMENT_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const ACCEPTED_ATTACHMENT_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".txt",
  ".md",
  ".csv",
  ".pdf",
  ".doc",
  ".docx",
  ".rtf",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
]);

export interface BugReportScreenshotMetadata {
  screenshot_bucket: string;
  screenshot_path: string;
  screenshot_name: string;
  screenshot_mime_type: string;
  screenshot_size_bytes: number;
}

type ScreenshotSupabaseClient = {
  storage: {
    from: (bucket: string) => {
      upload: (
        path: string,
        file: File,
        options: { contentType: string; upsert: boolean },
      ) => Promise<{ error: unknown | null }>;
      remove: (paths: string[]) => Promise<{ error: unknown | null }>;
    };
  };
};

function sanitizeFilename(name: string) {
  const normalized = name.trim().replace(/[^a-zA-Z0-9._-]/g, "_");
  return normalized || "attachment";
}

function getFileExtension(name: string) {
  const normalized = name.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf(".");
  if (dotIndex < 0) return "";
  return normalized.slice(dotIndex);
}

function getBugReportAttachmentContentType(file: File) {
  if (file.type) return file.type;

  switch (getFileExtension(file.name)) {
    case ".txt":
      return "text/plain";
    case ".md":
      return "text/markdown";
    case ".csv":
      return "text/csv";
    case ".pdf":
      return "application/pdf";
    case ".doc":
      return "application/msword";
    case ".docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case ".rtf":
      return "application/rtf";
    case ".ppt":
      return "application/vnd.ms-powerpoint";
    case ".pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case ".xls":
      return "application/vnd.ms-excel";
    case ".xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "application/octet-stream";
  }
}

export function validateBugReportScreenshot(file: File): { ok: true } | { ok: false; message: string } {
  const extension = getFileExtension(file.name);
  if (!ACCEPTED_ATTACHMENT_TYPES.has(file.type) && !ACCEPTED_ATTACHMENT_EXTENSIONS.has(extension)) {
    return { ok: false, message: "Attach a screenshot or document file." };
  }

  if (file.size > BUG_REPORT_SCREENSHOT_MAX_BYTES) {
    return { ok: false, message: "Attachment must be 5 MB or smaller." };
  }

  return { ok: true };
}

export function buildBugReportScreenshotPath(
  reporterUserId: string,
  file: File,
  screenshotId = crypto.randomUUID(),
) {
  return `${reporterUserId}/bug-reports/${screenshotId}-${sanitizeFilename(file.name)}`;
}

export async function uploadBugReportScreenshot(input: {
  supabase: ScreenshotSupabaseClient;
  reporterUserId: string;
  file: File;
  screenshotId?: string;
}): Promise<BugReportScreenshotMetadata> {
  const validation = validateBugReportScreenshot(input.file);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const path = buildBugReportScreenshotPath(input.reporterUserId, input.file, input.screenshotId);
  const contentType = getBugReportAttachmentContentType(input.file);
  const { error } = await input.supabase.storage.from(BUG_REPORT_SCREENSHOT_BUCKET).upload(path, input.file, {
    contentType,
    upsert: false,
  });

  if (error) throw error;

  return {
    screenshot_bucket: BUG_REPORT_SCREENSHOT_BUCKET,
    screenshot_path: path,
    screenshot_name: input.file.name,
    screenshot_mime_type: contentType,
    screenshot_size_bytes: input.file.size,
  };
}

export async function deleteBugReportScreenshot(
  supabase: ScreenshotSupabaseClient,
  metadata: Pick<BugReportScreenshotMetadata, "screenshot_bucket" | "screenshot_path">,
) {
  const { error } = await supabase.storage
    .from(metadata.screenshot_bucket)
    .remove([metadata.screenshot_path]);

  if (error) throw error;
}
